import { computeEvalOrder } from '../core/eval-order';
import type { WorldExpansion } from '../core/expansion';
import type { SimState } from '../core/state';
import { INTERVAL_COST, START_CASH, takeCost } from './economy';
import { COAL_SEAM, COPPER_MINE, IRON_RIDGE, SAND_DUNE, makeSource, sourceSpec } from './sources';

/**
 * Deliberately just wide enough for the setup arc — one source, a straight run of pipe, and the
 * Subscriber at the end of it. Everything beyond arrives via {@link STAGE1_EXPANSIONS}.
 */
export const STAGE1_GRID = { width: 7, height: 5 };
export const STAGE1_SINK_POS = { x: 5, y: 2 };
export const STAGE1_SOURCES = [sourceSpec(SAND_DUNE)];

export const IRON_RIDGE_EXPANSION_ID = 'iron-ridge';
export const COAL_SEAM_EXPANSION_ID = 'coal-seam';
export const COPPER_FLATS_EXPANSION_ID = 'copper-flats';
export const NORTH_RIDGE_EXPANSION_ID = 'north-ridge';
export const OPEN_FRONTIER_EXPANSION_ID = 'open-frontier';

/** Steel is the deepest chain the game can actually build, so producing it is what proves a player
 * has outgrown the yard the first three expansions fit in. */
export const COPPER_FLATS_STEEL_COUNT = 8;

/**
 * A playtest value, not a derived one. Iron accumulates far faster than slag did — an 80/20 yield
 * split, and slag gets filtered out — so the "twice is a pattern" count behind
 * `FILTER_UNLOCK_SLAG_SALE_COUNT` does not transfer.
 */
export const COAL_SEAM_IRON_SALE_COUNT = 12;

/**
 * The iron source is the first thing on the map that yields a byproduct, so it is what makes `filter`
 * worth unlocking — it has to arrive *after* the player has a working single-material line, or the
 * slag lesson lands before there is anything to filter it out of. Placing `take` earns the reveal;
 * the cash branch keeps a player who never places one from stalling out permanently.
 *
 * Coal Seam then makes joining worth unlocking: a second Observable is the only way to reach a
 * two-input recipe, since there are no splitters. Its gate is deliberately production rather than a
 * flag — every flag in the chain already fires an operator reveal, and reusing one would land two
 * reveals on a single trigger. Production instead decouples it from the operator ladder, so it
 * arrives on the player's own schedule: a player not yet running iron cannot trip it early, which
 * is correct, since Coal Seam is worth nothing to them yet.
 *
 * The last three rungs change what an expansion is *for*. The first three each teach an operator by
 * handing over the source that makes it worth placing, and stay small on purpose so the lesson has
 * nowhere to get lost. Once steel is running there is nothing left to teach, and the constraint
 * flips: what a player wants then is room to build wide, so these open ground faster than they add
 * material. Copper Flats still brings a source — the Copper Mine that {@link PLANNED_RECIPES}'
 * Motor has been waiting on — but North Ridge and Open Frontier are ground alone, gated on cash
 * because cash is the only signal that scales with an operation's size rather than its curriculum.
 */
export const STAGE1_EXPANSIONS: readonly WorldExpansion[] = [
  {
    id: IRON_RIDGE_EXPANSION_ID,
    gate: {
      kind: 'any',
      conditions: [
        { kind: 'flag', flag: 'everPlacedTake' },
        { kind: 'cash', threshold: takeCost(1) * 1.5 },
      ],
    },
    grid: { width: 11, height: 7 },
    sources: [sourceSpec(IRON_RIDGE)],
  },
  {
    id: COAL_SEAM_EXPANSION_ID,
    gate: {
      kind: 'any',
      conditions: [
        { kind: 'production', material: 'iron', count: COAL_SEAM_IRON_SALE_COUNT },
        { kind: 'cash', threshold: INTERVAL_COST * 1.5 },
      ],
    },
    grid: { width: 13, height: 9 },
    sources: [sourceSpec(COAL_SEAM)],
  },
  {
    id: COPPER_FLATS_EXPANSION_ID,
    gate: {
      kind: 'any',
      conditions: [
        { kind: 'production', material: 'steel', count: COPPER_FLATS_STEEL_COUNT },
        { kind: 'cash', threshold: INTERVAL_COST * 6 },
      ],
    },
    grid: { width: 24, height: 16 },
    sources: [sourceSpec(COPPER_MINE)],
  },
  {
    id: NORTH_RIDGE_EXPANSION_ID,
    gate: { kind: 'cash', threshold: INTERVAL_COST * 14 },
    grid: { width: 38, height: 25 },
    sources: [],
  },
  {
    id: OPEN_FRONTIER_EXPANSION_ID,
    gate: { kind: 'cash', threshold: INTERVAL_COST * 30 },
    grid: { width: 60, height: 40 },
    sources: [],
  },
];

/** The map at its largest, once every expansion in the ladder has been applied. */
export const FINAL_GRID = STAGE1_EXPANSIONS.reduce(
  (largest, expansion) => ({
    width: Math.max(largest.width, expansion.grid.width),
    height: Math.max(largest.height, expansion.grid.height),
  }),
  STAGE1_GRID,
);

export function createStage1State(): SimState {
  const state: SimState = {
    tick: 0,
    nextPacketId: 1,
    nextEntityId: 1,
    grid: { ...STAGE1_GRID },
    observables: {},
    sinks: {},
    subjects: {},
    machines: {},
    conveyors: {},
    packets: {},
    sources: STAGE1_SOURCES.map((source) => makeSource(source.position, source.yields)),
    economy: {
      cash: START_CASH,
      research: 0,
      peakCash: START_CASH,
      saleCount: 0,
      materialSaleCounts: {},
    },
    flags: {
      everPlacedFilter: false,
      everPlacedTake: false,
      everPlacedFrom: false,
      everPlacedInterval: false,
      everPlacedSubject: false,
    },
    appliedExpansions: [],
    evalOrder: [],
    base: null,
    subscriber: null,
    subscribed: false,
  };
  state.evalOrder = computeEvalOrder(state);
  return state;
}
